class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static async renderSectionFromFetch(url, event) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Facet section request failed (${response.status})`);
      const html = await response.text();
      FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
      FacetFiltersForm.renderSection(html, event);
    } catch (error) {
      console.error(error);
      FacetFiltersForm.clearLoading();
    }
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event);
  }

  static renderSection(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    try {
      FacetFiltersForm.renderFilters(parsedHTML, event);
    } catch (error) {
      console.error(error);
    }

    try {
      FacetFiltersForm.renderProductGridContainer(parsedHTML);
      FacetFiltersForm.renderProductCount(parsedHTML);
    } catch (error) {
      console.error(error);
      FacetFiltersForm.clearLoading();
    }
  }

  static clearLoading() {
    document.querySelectorAll('#ProductGridContainer .collection.loading, #ProductCount.loading, #ProductCountDesktop.loading, #ProductCountMobile.loading').forEach((element) => {
      element.classList.remove('loading');
    });
  }

  static filterSignature(root) {
    if (!root) return '';
    return [...root.querySelectorAll('.js-filter')].map((element) => {
      const named = element.querySelector('input[name], select[name]');
      return `${element.dataset.index}:${named ? named.name : ''}`;
    }).join('|');
  }

  static replaceFilterList(parsedHTML, selector) {
    const current = document.querySelector(selector);
    const next = parsedHTML.querySelector(selector);
    if (!current || !next) return false;
    if (FacetFiltersForm.filterSignature(current) === FacetFiltersForm.filterSignature(next)) return false;
    current.replaceWith(next);
    return true;
  }

  static renderProductGridContainer(parsedHTML) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const source = parsedHTML.getElementById('ProductGridContainer');
    if (!productGridContainer || !source) {
      FacetFiltersForm.clearLoading();
      return;
    }

    productGridContainer.innerHTML = source.innerHTML;
    if (document.querySelector('.js-load-more') || document.querySelector('.js-infinite-scroll')) {
      if (typeof loadMore === 'function') loadMore();
    }

    if (typeof colorSwatches === 'function') colorSwatches(productGridContainer);
    if (window.InlineCart?.init) window.InlineCart.init(productGridContainer);
  }

  static renderProductCount(parsedHTML) {
    const countSource = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    if (!countSource || !container) {
      FacetFiltersForm.clearLoading();
      return;
    }

    const count = countSource.innerHTML;
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const containerMobile = document.getElementById('ProductCountMobile');
    if (containerMobile) {
      containerMobile.innerHTML = count;
      containerMobile.classList.remove('loading');
    }
  }

  static renderFilters(parsedHTML, event) {
    // Section HTML can include filters the full search page omitted (price).
    // Updating by data-index then throws and leaves the grid under the overlay.
    ['#FacetsWrapperDesktop', '.mobile-facets__main'].forEach((selector) => {
      FacetFiltersForm.replaceFilterList(parsedHTML, selector);
    });

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (!target) return;
      target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const target = event?.target?.closest('.js-filter');
      if (target && target.isConnected) FacetFiltersForm.renderCounts(countsToRender, target);
    }

    FacetFiltersForm.enhanceFacetLists();
  }

  static normalizeFacetText(text) {
    return (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // Normalized search words padded with spaces, or '' outside the search page.
  static getSearchTerms() {
    const input = document.querySelector('#FacetFiltersForm input[name="q"], #FacetFiltersFormMobile input[name="q"]');
    if (!input || !input.value) return '';
    const normalized = FacetFiltersForm.normalizeFacetText(input.value.replace(/\s*-tag:\+18/g, ''));
    return normalized ? ` ${normalized} ` : '';
  }

  // Long search facets (e.g. every brand): selected and query-matching values first,
  // then by product count, with the long tail behind a "Ver mais" button.
  static enhanceFacetLists() {
    const terms = FacetFiltersForm.getSearchTerms();
    document.querySelectorAll('[data-facet-collapsible]:not([data-facet-enhanced])').forEach((list) => {
      list.dataset.facetEnhanced = 'true';
      if (!terms || /--(color|image)\b/.test(list.className)) return;

      const items = [...list.children].filter((child) => child.matches('li'));
      if (items.length <= FacetFiltersForm.FACET_VISIBLE_LIMIT) return;

      const ranked = items.map((item, index) => {
        const input = item.querySelector('input[type="checkbox"]');
        const label = item.querySelector('.facet-checkbox__label');
        const countElement = item.querySelector('.facet-checkbox__count');
        const count = countElement ? parseInt(countElement.textContent.replace(/\D/g, ''), 10) || 0 : 0;
        const name = FacetFiltersForm.normalizeFacetText(
          label ? [...label.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE).map((node) => node.textContent).join(' ') : ''
        );
        const active = Boolean(input && input.checked);
        const matches = name.length >= 3 && terms.includes(` ${name} `);
        return { item, index, count, tier: active ? 0 : matches ? 1 : 2 };
      });

      ranked.sort((a, b) => a.tier - b.tier || b.count - a.count || a.index - b.index);
      ranked.forEach(({ item }) => list.appendChild(item));

      const visibleCount = Math.max(FacetFiltersForm.FACET_VISIBLE_LIMIT, ranked.filter((entry) => entry.tier < 2).length);
      const extras = ranked.slice(visibleCount);
      if (!extras.length) return;

      extras.forEach(({ item }) => item.classList.add('facets-extra-item'));
      list.classList.add('facets-list--collapsible');

      const moreLabel = list.dataset.moreLabel || 'Ver mais';
      const toggleItem = document.createElement('li');
      toggleItem.className = 'facets-more';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'facets-more__button';
      button.setAttribute('aria-expanded', 'false');
      button.textContent = `${moreLabel} (${extras.length})`;
      toggleItem.appendChild(button);
      list.appendChild(toggleItem);
    });
  }

  static toggleFacetList(button) {
    const list = button.closest('ul');
    if (!list) return;
    const expanded = list.classList.toggle('facets-list--expanded');
    const extras = list.querySelectorAll('.facets-extra-item').length;
    const label = expanded ? (list.dataset.lessLabel || 'Ver menos') : `${list.dataset.moreLabel || 'Ver mais'} (${extras})`;
    button.textContent = label;
    button.setAttribute('aria-expanded', String(expanded));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const destination = document.querySelector(selector);
      if (!activeFacetsElement || !destination) return;
      destination.innerHTML = activeFacetsElement.innerHTML;
    })

    const chipsTop = document.querySelector('.facets__top--chips-only');
    if (chipsTop) {
      const hasFilterChips = Boolean(chipsTop.querySelector('.active-facets__button'));
      chipsTop.classList.toggle('facets__top--empty', !hasFilterChips);
    }

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const destination = document.querySelector(selector);
      if (!source || !destination) return;
      destination.innerHTML = source.innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.target.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'))
      this.onSubmitForm(searchParams, event)
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event)
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

}

FacetFiltersForm.filterData = [];
FacetFiltersForm.FACET_VISIBLE_LIMIT = 10;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.enhanceFacetLists();
document.addEventListener('click', (event) => {
  const button = event.target.closest('.facets-more__button');
  if (button) FacetFiltersForm.toggleFacetList(button);
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  fillSlider () {
    const inputRangeWrappers = document.querySelectorAll('.facets__price .facets__range');
    inputRangeWrappers.forEach(inputWrapper => {
      const inputsRange = inputWrapper.querySelectorAll('.field__range');
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];

      const range = inputRangeTo.max - inputRangeTo.min;
      const fromCurrent = inputRangeFrom.value - inputRangeTo.min;
      const toCurrent = inputRangeTo.value - inputRangeTo.min;
      const minRange = fromCurrent / range * 100;
      const maxRange = toCurrent / range * 100;

      inputWrapper.setAttribute(
        'style',
        `--range-min: ${minRange}%; --range-max: ${maxRange}%`
      );
    })
  }

  controlSlider () {
    const inputRangeWrappers = document.querySelectorAll('.facets__price .facets__range');
    const inputNumberWrappers = document.querySelectorAll('.facets__price .facets__price-wrapper');

    inputRangeWrappers.forEach((inputWrapper, index) => {
      const inputsRange = inputWrapper.querySelectorAll('.field__range');
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const inputNumberFrom = inputNumberWrappers[index].querySelectorAll('.field__input')[0];
      const inputNumberTo = inputNumberWrappers[index].querySelectorAll('.field__input')[1];

      inputRangeFrom.oninput = () => {
        const from = parseInt(inputRangeFrom.value, 10);
        const to = parseInt(inputRangeTo.value, 10);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        }
        else {
          inputNumberFrom.value = from;
        }

        this.fillSlider();
      }

      if (Number(inputRangeTo.value) <= 0 ) {
        inputRangeTo.style.zIndex = 2;
      } else {
        inputRangeTo.style.zIndex = 0;
      }

      inputRangeTo.oninput = () => {
        const from = parseInt(inputRangeFrom.value, 10);
        const to = parseInt(inputRangeTo.value, 10);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        }
        else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        if (Number(inputRangeTo.value) <= 0 ) {
          inputRangeTo.style.zIndex = 2;
        } else {
          inputRangeTo.style.zIndex = 0;
        }

        this.fillSlider();
      }
    })
  }

  controlInput() {
    const inputRangeWrappers = document.querySelectorAll('.facets__price .facets__range');
    const inputNumberWrappers = document.querySelectorAll('.facets__price .facets__price-wrapper');

    inputNumberWrappers.forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll('.field__input');
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom = inputRangeWrappers[index].querySelectorAll('.field__range')[0];
      const inputRangeTo = inputRangeWrappers[index].querySelectorAll('.field__range')[1];

      inputNumberFrom.oninput = () => {
        const from = parseInt(inputNumberFrom.value, 10);
        const to = parseInt(inputNumberTo.value, 10);
        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } 
        else {
          inputRangeFrom.value = from;
        }

        this.fillSlider();
      }

      inputNumberTo.oninput = () => {
        const from = parseInt(inputNumberFrom.value, 10);
        const to = parseInt(inputNumberTo.value, 10);
        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        }
        else {
          inputNumberTo.value = from;
        }

        this.fillSlider();
      }

    })
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
